import {
	type ApplicationListForResidenceData,
	applicationGetForResidence,
	applicationGetStatusForResidence,
	applicationListForResidence
} from '@repo/sdk/gen'
import { queryOptions } from '@tanstack/react-query'
import { makeQueryKeys } from '@/query/query-keys'

// The list and the detail of an application return students' personal data:
// every fetch is recorded in the event log as an access (decision D37). They
// keep the client defaults (no refetch when the window gets the focus, ten
// minutes before the data is stale): one opening of a page is one access, not
// one per focus. What changes while the page is open and holds no student,
// whether an application still counts, is read apart and kept fresh.

/** The applications sent to a residence of the manager. */
export function applicationsListQueryOptions(
	body: ApplicationListForResidenceData['body']
) {
	return queryOptions({
		queryKey: makeQueryKeys.applications.index(
			body.filters.residenceId,
			body
		),
		queryFn: ({ signal }) => applicationListForResidence({ body, signal }),
		// The previous page or filter of the same residence stays on screen while
		// the next loads. Another residence's rows never do: they are other
		// students' data.
		placeholderData: (previous, previousQuery) =>
			previousQuery?.queryKey[2] === body.filters.residenceId
				? previous
				: undefined
	})
}

/** One application of the residence with the data frozen at signing. */
export function applicationDetailQueryOptions(
	residenceId: string,
	submissionId: string
) {
	return queryOptions({
		queryKey: makeQueryKeys.applications.detail(residenceId, submissionId),
		queryFn: async ({ signal }) =>
			(
				await applicationGetForResidence({
					body: { data: { residenceId, submissionId } },
					signal
				})
			).data
	})
}

/**
 * Whether one application still counts (valid, replaced, withdrawn). No student
 * appears, so it is read again when the window gets the focus: the page of the
 * application is open while the student can withdraw it.
 */
export function applicationStatusQueryOptions(
	residenceId: string,
	submissionId: string
) {
	return queryOptions({
		queryKey: makeQueryKeys.applications.status(residenceId, submissionId),
		queryFn: async ({ signal }) =>
			(
				await applicationGetStatusForResidence({
					body: { data: { residenceId, submissionId } },
					signal
				})
			).data,
		staleTime: 0,
		refetchOnWindowFocus: true
	})
}
